import * as d3 from "d3";
import {
  EnrichedWithProtocolAnalysisRendererNode,
  GraphDataObject, SourceResourceProtocolData
} from "@app/netscope/model/data-models";
import {
  FlowsRendererInterface,
  NetscopeCommonFlowviewComponent,
} from "@app/netscope/netscope-common-flowview/netscope-common-flowview.component";
import {ProtocolAnalysisSelection} from "@app/netscope/model/flows-repository";

export class ProtocolAnalysisRenderer implements FlowsRendererInterface {
  flowComponent: NetscopeCommonFlowviewComponent;

  // Zoom of the view, and the <svg> listening to it
  zoomBehavior: d3.ZoomBehavior<any, any>;
  svgElement: d3.Selection<any, any, any, any>;
  viewCenter: [number, number] = [0, 0];

  labelsLevel = [
    'protocols',
    'protocol',
    'source',
    'destination'
  ];

  computeIdsAndLabels = (protocolData: EnrichedWithProtocolAnalysisRendererNode) => {
    protocolData.object.svg_id = this.computeSvgId(protocolData);
    protocolData.object.svg_label = this.computeLabel(protocolData);
    for (let child of protocolData.getChildren()) {
      this.computeIdsAndLabels(child);
    }
  }

  computeLabel = (data) => {
    // Compute the left part of the text inside circles.
    // By default, it uses the labelsLevel, and if a description is provided from the API response,
    // then it uses the label returned from the API.
    let label = this.labelsLevel[data.depth];
    if (data.object.type !== undefined) {
      label = data.object.type;
    }
    // Also, VMs can be source or destination. This information is set in "d.config.role" property.
    // It will override the value computed above.

    if (data.role !== undefined) {
      label = data.role;
    }
    return label;
  }

  fixUuidToBeUsedInCirclesId = (uuid) => {
    return uuid
      .replaceAll(":", "_")
      .replaceAll(" ", "__")
      .replaceAll(".", "_");
  }

  computeSvgId = (data) => {
    let prefix = "";
    if (data.object.parent !== null && data.object.parent !== undefined) {
      prefix = this.computeSvgId(data.object.parent);
    }
    let label = this.computeLabel(data);
    let fixedUuid = this.fixUuidToBeUsedInCirclesId(data.object.uuid);

    return `${prefix}_${label}_${fixedUuid}`;
  }

  setParents = (protocolData: EnrichedWithProtocolAnalysisRendererNode) => {
    for (let child of protocolData.getChildren()) {
      child.object.parent = protocolData
      this.setParents(child);
    }
  }

  async render(flowComponent: NetscopeCommonFlowviewComponent) {
    this.flowComponent = flowComponent;

    if (flowComponent.currentSelection.length == 0) {
      flowComponent.currentSelection = [{
        label: "reset: everything",
        whereClause: " true ",
        selectedColumn: "protocol"
      }];
    };

    let rootNode: SourceResourceProtocolData = await flowComponent.flowsRepository.getProtocolAnalysisData(flowComponent.currentSelection);

    let whereClauseForSelection = flowComponent.flowsRepository.computeWhereClauseForProtocolAnalysis(flowComponent.currentSelection);

    this.flowComponent.datatableTabFlowsRepository = this.flowComponent.flowsRepository.clone();
    this.flowComponent.datatableTabFlowsRepository.aggregated_flows_files = this.flowComponent.datatableTabFlowsRepository.flows_files;
    this.flowComponent.datatableTabFlowsRepository.setWhereClause(`where ${whereClauseForSelection}`);
    this.flowComponent.datatableTabFlowsRepository.showFirstPage();

    let hierarchy = {
      object: {
        svg_id: rootNode.object.name,
        svg_label: rootNode.object.name,
        name: rootNode.object.name,
        rendererNode: rootNode
      },
      size: 10,
      children: []
    }

    for (let child of rootNode.getChildren()) {
      let childPercent = 0;

      if (rootNode.size > 0) {
        childPercent = child.size / rootNode.size;
      }
      let childPercentStr = (100.0 * Math.round(childPercent * 100) / 100).toFixed(2) + "%";

      hierarchy.children.push({
        object: {
          svg_id: child.object.name,
          svg_label: child.object.name,
          name: childPercentStr,
          rendererNode: child
        },
        size: child.size,
        children: []
      })
    }

    if (hierarchy.children.length == 0) {
      hierarchy.children.push({
        object: {
          svg_id: hierarchy.object.name,
          svg_label: hierarchy.object.name,
          name: "100%",
          rendererNode: hierarchy.object.rendererNode
        },
        size: hierarchy.object.rendererNode.size,
        children: []
      });
    }

    console.log("protocol-analysis rendering (1)");

    // // Compute ids and labels on protocol config
    this.setParents(hierarchy.object.rendererNode);
    this.computeIdsAndLabels(hierarchy.object.rendererNode);
    console.log("protocol-analysis rendering (2)");

    let svgHeight = document.getElementById('divFlowsSvg').clientHeight;
    let svgWidth = document.getElementById('divFlowsSvg').clientWidth;

    d3.select('div#divFlowsSvg').select('svg').remove();
    flowComponent.svg = d3.select('div#divFlowsSvg')
      .append('svg')
      .attr('width', svgWidth)
      .attr('height', svgHeight)
      .append('g')
      .attr('id', 'graph_svg');

    const graphSvg = d3.select('g#graph_svg');
    graphSvg.selectAll('*').remove();

    if (svgHeight == 0) {
      svgHeight = flowComponent.lastSvgHeight;
    } else {
      flowComponent.lastSvgHeight = svgHeight;
    }

    if (svgWidth == 0) {
      svgWidth = flowComponent.lastSvgWidth;
    } else {
      flowComponent.lastSvgWidth = svgWidth;
    }

    if (svgHeight == 0) {
      svgHeight = flowComponent.lastSvgHeight;
    } else {
      flowComponent.lastSvgHeight = svgHeight;
    }

    if (svgWidth == 0) {
      svgWidth = flowComponent.lastSvgWidth;
    } else {
      flowComponent.lastSvgWidth = svgWidth;
    }

    const viewCenterX = svgWidth / 2;
    const viewCenterY = svgHeight / 2;

    const margin = 40;
    const diameter = Math.min(svgWidth, svgHeight);

    const color = d3.scaleLinear()
      .domain([0, 5])
      // @ts-ignore
      .range(['hsl(152,80%,80%)', 'hsl(228,30%,40%)'])
      // @ts-ignore
      .interpolate(d3.interpolateHcl);

    const pack = d3.pack()
      .size([svgWidth, svgHeight])
      .padding(2);
    console.log("protocol-analysis rendering (3)");

    const root = d3.hierarchy(hierarchy)
      .sum(function (d) {
        return d.size;
      });

    // const root = hierarchy;
    let focus = root;
    const nodes = pack(root).descendants();
    let view;

    // The circles are laid around the origin of the group: it is put at the center of the view
    const g = graphSvg.append('g')
      .attr('transform', 'translate(' + viewCenterX + ',' + viewCenterY + ')');

    const visuNode = g
      .selectAll('g')
      .data(nodes)
      .enter()
      .append('g')
      .attr('class', 'circleGroup');

    const self = this;
    console.log("protocol-analysis rendering (4)");

    const circle = visuNode
      .append('circle')
      .attr('class', function (d) {
        return d.parent ? d.children ? 'node' : 'node node--leaf' : 'node node--root';
      })
      // @ts-ignore
      .attr('id', (d) => d.data.object.svg_id)
      .style('fill', function (d) {
        return color(d.depth);
      })
      .style('cursor', 'pointer')
      // @ts-ignore
      .on('click', function (d, i) {
        if (focus !== i) {
          d.stopPropagation();
          // @ts-ignore
          let subSelection = i.data.object.rendererNode.object.subSelection;
          flowComponent.currentSelection.push(subSelection);

          flowComponent.reloadUi();
        }
      });

    const text = visuNode
      .append('text')
      .attr('id', (d) => {
        // @ts-ignore
        return `${d.data.object.svg_id}_text`;
      })
      .attr('class', 'textLabel')
      .style("text-anchor", "middle")
      .style('display', function (d) {
        return 'inline';
      })
      .text((d) => {
        // @ts-ignore
        let label = d.data.object.svg_label;
        // @ts-ignore
        return `${label}: ${d.data.object.name}`;
      })
      .each((d) => {
        // @ts-ignore
        d.circleId = d.data.object.svg_id;
        // @ts-ignore
        d.circle = document.getElementById(d.circleId);
        return d;
      })
      .style('cursor', 'pointer')
      // @ts-ignore
      .on('click', function (d, i) {
        if (focus !== i) {
          d.stopPropagation();
          // @ts-ignore
          let subSelection = i.data.object.rendererNode.object.subSelection;
          flowComponent.currentSelection.push(subSelection);
          flowComponent.reloadUi();
        }
      });

    const node = g.selectAll('circle,text');

    graphSvg
      .style('background', color(-1));

    let previousData;
    console.log("protocol-analysis rendering (5)");

    const zoom = (d) => {
      const focus0 = focus;
      focus = d;
      flowComponent.focusLevel = focus.depth + 1;
      flowComponent.focusNode = focus;

      // flowComponent.currentSelection = [];
      // let next = focus.data.object.rendererNode;
      // while (next !== undefined && next !== null) {
      //   flowComponent.currentSelection.unshift(next);
      //   next = next.parent;
      // }
      // flowComponent.askRefreshAngular();
      // flowComponent.updatePerProtocolFlows();

      let isMatchingNode = (node) => {
        if (flowComponent.currentSelection.indexOf(node) !== -1) {
          return true;
        }
        if (flowComponent.focusNode.children !== undefined && flowComponent.focusNode.children.indexOf(node) !== -1) {
          return true;
        }
        return false;
      };

      let isParentNode = (node) => {
        return flowComponent.currentSelection.indexOf(node) !== -1 && node.children !== undefined && node.children.length > 0;
      }

      let matchingVisuNode = visuNode
        .filter((d2) => isMatchingNode(d2));

      let nonMatchingVisuNode;
      if (previousData !== undefined) {
        nonMatchingVisuNode = previousData.matchingVisuNode;
      } else {
        nonMatchingVisuNode = visuNode
          .filter((d2) => !isMatchingNode(d2));
      }

      let parentNodes = visuNode
        .filter((d2) => isParentNode(d2));

      setTimeout(() => {
        const transition = d3.transition()
          // @ts-ignore
          .duration(750)
          .tween('zoom', function (d2) {
            // @ts-ignore
            const i = d3.interpolateZoom(view, [focus.x, focus.y, focus.r * 2 + margin]);
            return function (t) {
              zoomTo(i(t));
            };
          });
      }, 50);

      nonMatchingVisuNode
        .selectAll('.textLabel')
        .style('display', (d2) => 'none');

      // Hide other circles
      nonMatchingVisuNode
        .selectAll('circle')
        .style('display', (d2) => 'none');

      // Show circles that are in [0, this.focusLevel] and the circles just below
      matchingVisuNode
        .selectAll('circle')
        .style('display', (d2) => '');

      matchingVisuNode
        .selectAll('.textLabel')
        .style('display', (d2) => 'inline');

      if (root.children.length > 0) {
        parentNodes
          .selectAll('.textLabel')
          .style('display', (d2) => 'none');
      }

      let cacheTextSize = {};

      function updateTextSize() {
        const text2 = matchingVisuNode
          .select('.textLabel')
          .filter((d2) => d2 !== focus.parent)
          .style("font-size", (d, i) => {

            // Optimization for when there are many circles
            if (d.r < 1) {
              return "0px";
            }

            // @ts-ignore
            let textId = `${d.circleId}_text`;
            let textSvgElement = document.getElementById(textId);

            // @ts-ignore
            var bb = textSvgElement.getBBox();

            return d.r * (bb.height / bb.width) + "px";
          });
      }

      updateTextSize();
    };
    console.log("protocol-analysis rendering (6)");

    function zoomTo(v) {
      const k = diameter / v[2];
      view = v;
      // @ts-ignore
      node.attr('transform', function (d) {
        // @ts-ignore
        return 'translate(' + (d.x - v[0]) * k + ',' + (d.y - v[1]) * k + ')';
      });
      circle.attr('r', function (d) {
        return d.r * k;
      });
    }

    // let ratioMinSizeOverMaxSize = 10;

    function zoomed({
      transform
    }) {
      console.log("transform: " + transform);
      g.attr("transform", transform);
    }

    // The zoom listens to the whole <svg>: a <g> only gets the events falling on what it draws, so zooming and
    // moving used to work over the circles only
    this.zoomBehavior = d3.zoom()
        .extent([
            [0, 0],
            [svgWidth, svgHeight]
        ])
        // .scaleExtent([0.01, ratioMinSizeOverMaxSize])
        .on("zoom", zoomed);
    this.svgElement = d3.select('div#divFlowsSvg').select('svg');
    this.svgElement.call(this.zoomBehavior);
    this.viewCenter = [viewCenterX, viewCenterY];

    console.log("protocol-analysis rendering (7)");
    // @ts-ignore
    zoomTo([root.x, root.y, root.r * 2 + margin]);
    console.log("protocol-analysis rendering (8)");
    zoom(root);
    console.log("protocol-analysis rendering (9)");

    // flowComponent.currentSelection = [];
    // let next = root.data.object.rendererNode;
    // while (next !== undefined && next !== null) {
    //   flowComponent.currentSelection.unshift(next);
    //   next = next.parent;
    // }
    // console.log("protocol-analysis rendering (10)");

    flowComponent.zoomFunction = zoom;
    flowComponent.askRefreshAngular();

    // if (zoomedProtocols !== undefined) {
    //   circle.filter((d) => {
    //     // @ts-ignore
    //     return d.data.object.name === zoomedProtocols;
    //   }).each((n) => {
    //     setTimeout(() => {
    //       zoom(n);
    //     }, 300);
    //   })
    // }
    console.log("protocol-analysis rendering (11)");

    // step1: zoom out, and let d3 recompute positions and sizes
    this.resetView();

    // Update legend
    flowComponent.updateLegend(undefined);

    let innerHtmlBefore = document.getElementById("divFlowsSvg").innerHTML;

    // Bug: after displaying clusters-viewer, sometimes, the visualization disappears
    setTimeout(() => {
      let before = innerHtmlBefore;
      let innerHtml = document.getElementById("divFlowsSvg").innerHTML;
      if (innerHtml.indexOf("svg") == -1) {
        console.warn("re-rendering protocol analysis : sigmaJS may have taken time to clean its states, thus overlapping with protocol analysis.");
        this.render(flowComponent);
      }
    }, 200);
  }

  // Puts the circles back at the center of the view, unzoomed
  resetView = () => {
    if (this.svgElement == undefined) {
      return;
    }
    let [x, y] = this.viewCenter;
    this.svgElement.call(this.zoomBehavior.transform, d3.zoomIdentity.translate(x, y).scale(1.0));
  }

  zoomInVisualisation = (flowComponent: NetscopeCommonFlowviewComponent) => {
    this.svgElement?.transition().duration(250).call(this.zoomBehavior.scaleBy, 1.25);
  }

  zoomOutVisualisation = (flowComponent: NetscopeCommonFlowviewComponent) => {
    this.svgElement?.transition().duration(250).call(this.zoomBehavior.scaleBy, 1 / 1.25);
  }

  recenterVisualisation = (flowComponent: NetscopeCommonFlowviewComponent) => {
    this.resetView();
  }

  destroy(flowComponent: NetscopeCommonFlowviewComponent): void {}

  update(flowComponent: NetscopeCommonFlowviewComponent): void {}

  isOutdated(): boolean {
    return this.flowComponent.selectedView !== "protocol-analysis";
  }
}

